import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import PageHeader from "@/pages/_shared/PageHeader";
import { Link } from "react-router-dom";
import { FadeUp, Stagger, StaggerItem, SlideUpLine } from "@/pages/_shared/anim";
import Technician from "../../../src/assets/About1.jpg";

const values = [
  {
    icon: "ri-lightbulb-flash-line",
    title: "Technical Clarity",
    desc: "Understand the problem before recommending the solution.",
  },
  {
    icon: "ri-tools-line",
    title: "Practical Solutions",
    desc: "Focus on solutions that make technical and commercial sense.",
  },
  {
    icon: "ri-stack-line",
    title: "Multi-Brand Thinking",
    desc: "Approach elevator requirements without being restricted to a single manufacturer.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Safety & Reliability",
    desc: "Support safer, more reliable elevator and escalator operation through technical inspection and informed recommendations.",
  },
];

const solutionJourney = [
  { step: "Requirement", icon: "ri-clipboard-line" },
  { step: "Assessment", icon: "ri-search-line" },
  { step: "Solution", icon: "ri-lightbulb-line" },
  { step: "Implementation", icon: "ri-settings-3-line" },
  { step: "Long-Term Support", icon: "ri-shield-check-line" },
];

const areas = [
  { icon: "ri-settings-3-line", title: "Technical Solutions" },
  { icon: "ri-building-2-line", title: "Supply & Installation" },
  { icon: "ri-tools-line", title: "Maintenance & Upgradation" },
  { icon: "ri-stack-line", title: "All-Brand Spare Parts" },
];

export default function AboutPage() {
  return (
    <div className="min-w-0 bg-crest-cream">
      <Navbar />
      <main>
        <PageHeader
          eyebrow="Who we are"
          title={
            <>
              More Than Spare Parts.
              <br />
              <span className="text-white">A Complete Elevator Solutions Hub.</span>
            </>
          }
          description="Uplift One provides technical solutions, supply and installation, maintenance and upgradation, and all-brand spare parts for elevators and escalators."
          crumbs={[
            { label: "Home", to: "/" },
            { label: "About" },
          ]}
          meta={[
            { label: "Core pillars", value: "4" },
            { label: "Brand support", value: "Multi" },
            { label: "Location", value: "Abu Dhabi – UAE" },
            { label: "Coverage", value: "UAE" },
          ]}
          accentImage={Technician}
        />

        {/* Mission */}
        <section className="bg-crest-cream py-20 md:py-24">
          <div className="px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
              <div className="lg:col-span-5">
                <FadeUp>
                  <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] text-crest-deep/60 uppercase">
                    <span className="w-6 h-px bg-crest-deep/40" /> Our mission
                  </span>
                </FadeUp>
                <h2 className="font-display text-4xl md:text-5xl text-crest-deep leading-tight mt-5">
                  <SlideUpLine as="div">Keep People Moving.</SlideUpLine>
                  <SlideUpLine as="div" delay={0.08}>Keep Systems</SlideUpLine>
                  <SlideUpLine as="div" delay={0.16}>
                    <span className="text-crest-bg">Performing.</span>
                  </SlideUpLine>
                </h2>
              </div>
              <FadeUp delay={0.15} className="lg:col-span-7 text-crest-deep/75 text-lg leading-relaxed space-y-5">
                <p>
                  Our mission is to provide practical, technically sound, and cost-conscious elevator and escalator solutions — from installation and maintenance to specialized technical support and spare parts.
                </p>
                <p>
                  Our goal is to bring technical knowledge and practical solutions together — helping customers address requirements across the complete lifecycle of vertical and inclined transportation systems.
                </p>

                <Stagger className="grid grid-cols-2 lg:grid-cols-4 gap-4 pt-4" stagger={0.1}>
                  {areas.map((a) => (
                    <StaggerItem key={a.title}>
                      <div className="bg-white rounded-lg border border-black/5 p-4 text-center">
                        <span className="w-10 h-10 flex items-center justify-center rounded-md bg-crest-orange text-white mx-auto">
                          <i className={`${a.icon} text-lg`}></i>
                        </span>
                        <div className="text-xs text-crest-deep/70 mt-2 font-medium">
                          {a.title}
                        </div>
                      </div>
                    </StaggerItem>
                  ))}
                </Stagger>
              </FadeUp>
            </div>
          </div>
        </section>

        {/* Solution Journey (replaces Timeline) */}
        <section className="bg-white py-20 md:py-24">
          <div className="px-6 md:px-12">
            <FadeUp className="max-w-2xl mb-14">
              <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] text-crest-deep/60 uppercase">
                <span className="w-6 h-px bg-crest-deep/40" /> The Uplift Solution Journey
              </span>
              <h2 className="font-display text-4xl md:text-5xl text-crest-deep leading-tight mt-5">
                From requirement to long-term support.
              </h2>
            </FadeUp>

            <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" stagger={0.1}>
              {solutionJourney.map((s, i) => (
                <StaggerItem
                  key={s.step}
                  className="relative bg-crest-cream rounded-lg border border-black/5 p-6 text-center"
                >
                  <span className="w-12 h-12 flex items-center justify-center rounded-full bg-crest-deep text-crest-orange mx-auto">
                    <i className={`${s.icon} text-xl`}></i>
                  </span>
                  <div className="font-display text-2xl text-crest-deep mt-4">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="font-display text-lg text-crest-deep mt-2">{s.step}</h3>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>

        {/* Values */}
        <section className="bg-crest-cream py-20 md:py-24">
          <div className="px-6 md:px-12">
            <FadeUp className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
              <h2 className="font-display text-4xl md:text-5xl text-crest-deep leading-tight max-w-xl">
                What Drives Our Approach.
              </h2>
              <p className="text-crest-deep/70 max-w-md text-base leading-relaxed">
                Four principles that guide how we assess requirements, recommend solutions, and support our customers.
              </p>
            </FadeUp>
            <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" stagger={0.08}>
              {values.map((v) => (
                <StaggerItem
                  key={v.title}
                  className="bg-white border border-black/5 rounded-lg p-6 hover:border-crest-orange transition-colors"
                >
                  <span className="w-11 h-11 flex items-center justify-center rounded-md bg-crest-deep text-crest-orange">
                    <i className={`${v.icon} text-xl`}></i>
                  </span>
                  <h3 className="font-display text-xl text-crest-deep mt-5">
                    {v.title}
                  </h3>
                  <p className="text-sm text-crest-deep/70 mt-2 leading-relaxed">
                    {v.desc}
                  </p>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-crest-cream py-16 md:py-20">
          <div className="px-6 md:px-12">
            <FadeUp>
              <div className="bg-crest-orange rounded-xl p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                <div className="max-w-xl">
                  <h3 className="font-display text-3xl md:text-4xl text-crest-deep leading-tight">
                    Have an elevator project or technical question?
                  </h3>
                  <p className="text-crest-deep/75 mt-3">
                    Share your requirement with us — our team will help identify the right solution for your needs.
                  </p>
                </div>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-crest-deep text-crest-orange px-7 py-3.5 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap hover:bg-crest-bg transition-colors"
                >
                  Talk to our team
                  <i className="ri-arrow-right-up-line"></i>
                </Link>
              </div>
            </FadeUp>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}